import type { SetState } from "zustand";

import type { AskSource } from "@performyard/service-contracts";

import type { ChatMessageAttachment, ChatStatus, ConfidenceLevel, StreamStage } from "../../types";
import type { AskPerformYardState } from "../types";

import { COMPETENCY_IMPORT_INTRO } from "../../constants";
import { buildGreetingMessage, generateMessageId, getOrCreateSessionId } from "../helpers";
import type { GreetingFlags } from "../helpers";
import { drainPendingChunks, schedulePendingChunk } from "../streaming-buffer";

export const createChatSlice = (set: SetState<AskPerformYardState>) => ({
	sessionId: getOrCreateSessionId(),
	messages: [
		buildGreetingMessage({
			askHrEnabled: false,
			goalCreationEnabled: false,
			formCreationEnabled: false,
			competencyInterviewerEnabled: false,
			reportBuilderEnabled: false,
			isOnFormBuilderPage: false,
			isOnGoalCreationPage: false,
			goalCoachingEnabled: false,
			isOnCompetencyPage: false,
			formBuildingCoachEnabled: false,
		}),
	],
	status: "idle" as ChatStatus,
	streamStage: null as StreamStage | null,
	streamMessage: null as string | null,
	error: null as string | null,
	inputValue: "",

	addUserMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "user" as const,
					content,
					timestamp: new Date(),
				},
			],
			inputValue: "",
		}));
		return id;
	},

	addStaticAssistantMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [...state.messages, { id, role: "assistant" as const, content, timestamp: new Date() }],
		}));
		return id;
	},

	addImportNarrationMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "assistant" as const,
					content,
					isStreaming: true,
					isImportProgress: true,
					timestamp: new Date(),
				},
			],
		}));
		return id;
	},
	updateImportNarrationMessage: (messageId: string, content: string, isStreaming: boolean) => {
		set((state) => {
			const idx = state.messages.findIndex((m) => m.id === messageId);
			if (idx === -1) return state;
			const updated = state.messages.slice();
			updated[idx] = { ...updated[idx], content, isStreaming };
			return { messages: updated };
		});
	},

	setCompetencyUploadPromptMessage: (messageId: string | null) => {
		set((state) => ({
			messages: state.messages.map((message) => {
				const showCompetencyUpload = message.id === messageId;
				if ((message.showCompetencyUpload ?? false) === showCompetencyUpload) return message;
				return { ...message, showCompetencyUpload };
			}),
		}));
	},

	addAttachmentUserMessage: (attachment: ChatMessageAttachment) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{ id, role: "user" as const, content: "", attachment, timestamp: new Date() },
			],
		}));
		return id;
	},
	updateAttachmentState: (messageId: string, patch: Partial<ChatMessageAttachment>) => {
		set((state) => {
			const idx = state.messages.findIndex((m) => m.id === messageId);
			if (idx === -1 || !state.messages[idx].attachment) return state;
			const updated = state.messages.slice();
			updated[idx] = { ...updated[idx], attachment: { ...updated[idx].attachment!, ...patch } };
			return { messages: updated };
		});
	},

	markCompetencyIntro: (messageId: string) => {
		set((state) => {
			const idx = state.messages.findIndex((m) => m.id === messageId);
			if (idx === -1) return state;
			const updated = state.messages.slice();
			updated[idx] = { ...updated[idx], content: COMPETENCY_IMPORT_INTRO, showCompetencyUpload: true };
			return { messages: updated };
		});
	},

	// Marks a message final without ending the turn — `status` is left for `done` to flip.
	finalizeMessage: (messageId: string) => {
		set((state) => {
			const idx = state.messages.findIndex((m) => m.id === messageId);
			if (idx === -1) return state;
			const updated = state.messages.slice();
			updated[idx] = { ...updated[idx], isStreaming: false };
			return { messages: updated };
		});
	},

	startAssistantMessage: () => {
		const id = generateMessageId();
		// Pushes the empty streaming assistant message so the in-message
		// "Thinking…" placeholder can render immediately. Status is intentionally
		// left untouched so callers control the thinking→streaming transition
		// separately (the in-message placeholder reads `streamMessage` regardless,
		// so the bubble keeps showing live tool/status text either way).
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "assistant" as const,
					content: "",
					isStreaming: true,
					timestamp: new Date(),
				},
			],
		}));
		return id;
	},

	appendToAssistantMessage: (messageId: string, chunk: string) => {
		schedulePendingChunk(messageId, chunk);
	},

	completeAssistantMessage: (
		messageId: string,
		sources?: AskSource[],
		confidence?: ConfidenceLevel,
		isIncomplete?: boolean
	) => {
		const remainingChunks = drainPendingChunks();

		set((state) => {
			const lastIdx = state.messages.length - 1;
			const lastMsg = state.messages[lastIdx];
			if (!lastMsg || lastMsg.id !== messageId) return state;

			const finalContent = lastMsg.content + remainingChunks;

			// Drop tool-only assistant turns (no content AND no meaningful payload).
			// Tool-only steps with suppressed narration finish with no chunks. Bedrock
			// rejects messages with empty content on re-send, so the backend agent
			// `formatMessages` helpers filter empty turns before they reach the model
			// (mirrors interviewer/handler.ts:44-46). That means we can safely keep
			// empty bubbles here when they own UI payload — the backend will strip
			// them on the next request.
			//
			// Messages own UI payload when they carry sources, an isIncomplete flag,
			// suggestion arrays, OR the active reportBuilder reference (whose
			// `messageId` anchors the saving/error/Open-in-R&A CTAs in ChatMessage).
			// Confidence is NOT user-visible (backend-only signal for source filtering),
			// so it must not preserve an otherwise-empty turn.
			const ownsReportBuilder = state.reportBuilder?.messageId === messageId;
			const hasMeaningfulPayload =
				!!sources?.length ||
				isIncomplete === true ||
				!!lastMsg.competencySuggestions?.length ||
				!!lastMsg.goalSuggestions?.length ||
				!!lastMsg.goalImprovements?.length ||
				!!lastMsg.formSuggestions?.length ||
				!!lastMsg.formQuestionSuggestions?.length ||
				!!lastMsg.formImprovements?.length ||
				ownsReportBuilder;
			if (finalContent.trim().length === 0 && !hasMeaningfulPayload) {
				return {
					messages: state.messages.slice(0, -1),
					status: "idle" as const,
					streamStage: null,
					streamMessage: null,
				};
			}

			const updated = state.messages.slice();
			updated[lastIdx] = {
				...lastMsg,
				content: finalContent,
				isStreaming: false,
				sources,
				confidence,
				isIncomplete,
			};
			return { messages: updated, status: "idle" as const, streamStage: null, streamMessage: null };
		});
	},

	setStatus: (status: ChatStatus) => set({ status }),
	setStreamStage: (stage: StreamStage | null) => set({ streamStage: stage }),
	setStreamMessage: (message: string | null) => set({ streamMessage: message }),
	setError: (error: string | null) =>
		set((state) => ({
			error,
			status: error ? "error" : state.status === "error" ? "idle" : state.status,
		})),

	updateGreetingMessage: (flags: GreetingFlags) => {
		set((state) => {
			const cached = {
				cachedAskHrEnabled: flags.askHrEnabled,
				cachedGoalCreationEnabled: flags.goalCreationEnabled,
				cachedFormCreationEnabled: flags.formCreationEnabled,
				cachedCompetencyInterviewerEnabled: flags.competencyInterviewerEnabled,
				cachedReportBuilderEnabled: flags.reportBuilderEnabled,
				cachedIsOnFormBuilderPage: flags.isOnFormBuilderPage,
				cachedGoalCoachingEnabled: flags.goalCoachingEnabled,
				cachedIsOnGoalCreationPage: flags.isOnGoalCreationPage,
				cachedFormBuildingCoachEnabled: flags.formBuildingCoachEnabled,
				cachedIsOnCompetencyPage: flags.isOnCompetencyPage,
			};
			const greetingIdx = state.messages.findIndex((m) => m.id === "greeting");
			if (greetingIdx === -1) return { messages: state.messages, ...cached };
			const updatedMessages = state.messages.slice();
			updatedMessages[greetingIdx] = buildGreetingMessage(flags);
			return { messages: updatedMessages, ...cached };
		});
	},
});
